import * as React from "react";
import {
    getGridSingleSelectOperators,
    GridColumns,
    GridFilterModel,
    GridRenderCellParams,
} from "@mui/x-data-grid-pro";
import { useSelector } from "react-redux";
import { updateSessionsGridState } from "../../app/slices/sessionListTableStateSlice";
import CclPersistentDataGrid, { CclPersistantDataGridMiscButton } from "../../components/common/cclPersistentDataGrid/cclPersistentDataGrid";
import { EventDocument } from "../../services/types/search.service.types";
import { addCustomDataGridFilters } from "../../components/common/customDataGridFilters/customDataGridFilters";
import CclCommonLink from "../../components/common/cclCommonLink";
import { eGroupIds, UserClaimsService } from "../../services/currentUserService/currentUserService";

type FilterItem = { columnField: string; operatorValue?: string; value?: any };

function mergeFilterModels(active: GridFilterModel | undefined, incoming: GridFilterModel | undefined): GridFilterModel | undefined {
    if (!active) return incoming ?? undefined;
    if (!incoming) return active;
    const map = new Map<string, FilterItem>();
    (active.items || []).forEach(i => map.set(i.columnField, i));
    (incoming.items || []).forEach(i => map.set(i.columnField, i));
    return { items: Array.from(map.values()) };
}

export interface SessionsListProps {
    sessions: EventDocument[] | undefined;
    loading: boolean;
    createSession: () => void;
    openClientSearch: () => void;
    filterModel?: GridFilterModel | undefined;
}

export const SessionsDataGridPro: React.FC<SessionsListProps> = (props) => {
    const stateSelector = useSelector((state: any) => state.sessionListTableState);
    const [sessions, setSessions] = React.useState<EventDocument[] | undefined>(props.sessions);
    const claimservice = new UserClaimsService();
    const isClientAdmin = claimservice.IsUserInGroups([eGroupIds.eClientAdmin]);
    const isNwaAdmin = claimservice.IsUserNwaAdmin();
    const isAssessmentAdmin = claimservice.GetCurrentUserAssesmentAdminGroupId() > 0;

    const [showActiveOnly, setShowActiveOnly] = React.useState<boolean>(false);

    let COLUMNS = [
        {
            field: "eventnumber",
            headerName: "Session ID",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/session/${params.row.eventnumber}`}
                />
            ),
            width: 125,
        },
        {
            field: "projectCode",
            headerName: "Project ID",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/session/${params.row.eventnumber}`}
                />
            ),
            width: 125,
        },
        {
            field: "title",
            headerName: "Title",
            type: "string",
            renderCell: (params: GridRenderCellParams) => (
                <CclCommonLink
                    text={params.value}
                    keyVal={params.value}
                    to={`/session/${params.row.eventnumber}`}
                />
            ),
            flex: 1,
        },
        {
            field: "statusKey",
            headerName: "Status",
            type: "string",
            valueGetter: (params: any) => {
                switch (params.row.statusKey) {
                    case "1":
                        return "Active";
                    case "2":
                        return "Cancelled";
                    case "3":
                        return "Draft";
                    case "4":
                        return "In Setup";
                }
                return "Unknown";
            },
            flex: 1,
        },
        {
            field: "startdate",
            headerName: "Start",
            valueGetter: (params: any) => new Date(params.row.startdate.slice(0, -1)),
            type: "date",
            renderCell: (params: GridRenderCellParams) => {
                if (params.row.startdate !== null) {
                    const dt = params.value as Date;
                    if (
                        !(dt.getFullYear() === 9999 && dt.getMonth() === 11 && dt.getDate() === 31)
                    ) {
                        return <span>{dt.toLocaleDateString()}</span>;
                    }
                }
                return <span></span>;
            },
            width: 150,
        },
        {
            field: "endDate",
            headerName: "End",
            valueGetter: (params: any) => new Date(params.row.endDate.slice(0, -1)),
            type: "date",
            renderCell: (params: GridRenderCellParams) => {
                if (params.row.endDate !== null) {
                    const dt = params.value as Date;
                    if (
                        !(dt.getFullYear() === 9999 && dt.getMonth() === 11 && dt.getDate() === 31)
                    ) {
                        return <span>{dt.toLocaleDateString()}</span>;
                    }
                }
                return <span></span>;
            },
            width: 150,
        },
        {
            field: "typeName",
            headerName: "Type",
            type: "string",
            width: 150,
        },
        {
            field: "programCode",
            headerName: "Code",
            type: "string",
            width: 50,
        },
        {
            field: "campusName",
            headerName: "Managing Location",
            width: 175,
            type: "string",
        },
        {
            field: "location",
            headerName: "Delivery Location",
            type: "string",
            width: 175,
        },
        {
            field: "hostPlatformName",
            headerName: "Hosting Platform",
            type: "string",
            width: 200,
        },
        {
            field: "surveyPlatformName",
            headerName: "Assessment Platform",
            type: "string",
            width: 200,
        },
        {
            field: "hasCompassSku",
            headerName: "Compass",
            valueOptions: [
                { value: true, label: "Yes" },
                { value: false, label: "No" },
            ],
            filterOperators: [...getGridSingleSelectOperators().filter((op) => op.value === "is")],
            renderCell: (params: GridRenderCellParams) => {
                return params.value === true ? "Yes" : "No";
            },
            width: 85,
        },
        {
            field: "clientOrganization",
            headerName: "Customer",
            type: "string",
            width: 200,
        }
    ];

    React.useLayoutEffect(() => {
        setSessions(props.sessions);
    }, [props.sessions]);

    let BUTTONS: CclPersistantDataGridMiscButton[] = [
        {
            id: "client-adv-search-button",
            caption: "Search",
            onClick: props.openClientSearch,
            returnSelected: true,
            restricted: true,
        },
        {
            id: "create-new-session-button",
            caption: "Create Session",
            onClick: props.createSession,
            returnSelected: true,
            restricted: true,
        },
        {
            id: "show-active-only-switch",
            caption: "Show Only Active",
            type: "switch",
            checkedState: showActiveOnly,
            onToggled: (checked: boolean) => setShowActiveOnly(checked),
            onClick: () => {},
            returnSelected: false,
            restricted: false,
            hidden: isClientAdmin,
        },
    ];
    const CLIENT_ADMIN_EXCLUDED_FIELDS = new Set([
        "programCode",
        "projectCode",
        "statusKey",
        "campusName",
        "location",
        "hostPlatformName",
        "surveyPlatformName",
        "hasCompassSku",
        "endDate",
    ]);

    if (isClientAdmin) {
        COLUMNS = COLUMNS.filter((x) => !CLIENT_ADMIN_EXCLUDED_FIELDS.has(x.field)).map((x) => {
            if (x.field === "startdate") return { ...x, headerName: "Date", width: undefined, flex: 0.35 };
            if (x.field === "typeName") return { ...x, width: undefined, flex: 0.35 };
            if (x.field === "clientOrganization") return { ...x, width: undefined, flex: 0.5 };
            return x;
        }) as typeof COLUMNS;
    }

    if (isNwaAdmin || !isAssessmentAdmin) {
        BUTTONS = BUTTONS.filter((x) => x.id !== "create-new-session-button");
    }

    if (!isClientAdmin) {
        BUTTONS = BUTTONS.filter((x) => x.id !== "client-adv-search-button");
    }

    const activeOnlyFilterModel: GridFilterModel | undefined = showActiveOnly
        ? { items: [{ columnField: "statusKey", operatorValue: "equals", value: "Active" }] }
        : undefined;

    const mergedFilterModel = React.useMemo(
        () => mergeFilterModels(activeOnlyFilterModel, props.filterModel ?? undefined),
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [showActiveOnly, props.filterModel]
    );

    return (
        <CclPersistentDataGrid
            columns={addCustomDataGridFilters(COLUMNS as GridColumns<any>)}
            data={sessions ?? []}
            loading={props.loading}
            stateSelector={stateSelector}
            userPreferenceId={1}
            getRowIdFn={(row: EventDocument) => row.eventnumber}
            actionCreator={updateSessionsGridState}
            hideToolbar={false}
            initState={JSON.stringify({
                sorting: { sortModel: [{ field: "startdate", sort: "desc" }] },
            })}
            resultBar={false}
            resultBarText={""}
            miscButtons={BUTTONS}
            hideFooter={false}
            columnVisibility={{
                clientOrganization: false
            }}
            filterModel={mergedFilterModel}
        />
    );
};

export default SessionsDataGridPro;
